// Libs
import moment from 'moment';
import classNames from 'classnames/bind';
import { useSelector } from 'react-redux';
import { useDroppable } from '@dnd-kit/core';
import { useTranslation } from 'react-i18next';
import { Box, Button, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
// Components, Layouts, Pages
import { ItemSchedule } from '~/components/module/schedule';
import { BaseScheduleKanban, BaseSelect, Loading } from '~/components/common';
// Others
import { EMPTY_STRING } from '~/utils/constants/common';
import { DateFormatEnum } from '~/utils/enum/common';
import { useAppDispatch } from '~/utils/redux/hooks';
import { convertScheduleKanban } from './helper';
import { scheduleActions, selectIsRefreshScheduleList } from '~/thunks/schedule/scheduleSlice';
import { cleanEmptyFields, formatDate, getCurrentWeekRange } from '~/utils/helper/common';
import { IEventSchedule, IQueryParamSchedule, IScheduleList } from '~/utils/interface/schedule';
import { changeEventSchedule, exportSchedule, getListSchedule } from '~/thunks/schedule/scheduleThunk';
import { ChangeData, ColumnTypeKanban, IBaseOption, IRangeDate, IResultCallApiFn } from '~/utils/interface/common';
// Styles, images, icons
import styles from './VehicleSchedule.module.scss';
import { icons } from '~/assets';

type Props = {
  rangeDate: IRangeDate;
  isView?: boolean;
  setRangeDate: Dispatch<SetStateAction<IRangeDate>>;
};

const cx = classNames.bind(styles);

const VehicleSchedule = (props: Props) => {
  //#region Destructuring Props
  const { rangeDate, isView = false, setRangeDate } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/schedule', 'common/base']);
  const dispatch = useAppDispatch();
  //#endregion Declare Hook

  //#region Selector
  const isRefreshListSchedule = useSelector(selectIsRefreshScheduleList);
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDisableSchedule, setIsDisableSchedule] = useState<boolean>(false);
  const [dataSchedule, setDataSchedule] = useState<ColumnTypeKanban<IScheduleList>[]>([]);
  const { setNodeRef, isOver } = useDroppable({
    id: 'vehicleScheduleZone',
  });
  //#endregion Declare State

  //#region Declare Memo
  const selectedWeekValue = useMemo<string>(() => {
    if (!rangeDate?.startDate || !rangeDate?.endDate) return EMPTY_STRING;

    const startDate = moment(rangeDate.startDate);
    const endDate = moment(rangeDate.endDate);
    const weekNumber = startDate.week();

    let year = startDate.year();
    if (weekNumber === 1 && startDate.month() === 11) {
      year = year + 1;
    }

    const currentYear = moment().year();
    if (year !== currentYear) {
      return EMPTY_STRING;
    }

    const startOfWeek = moment().year(year).week(weekNumber).startOf('week').format(DateFormatEnum.YYYY_MM_DD);
    const endOfWeek = moment().year(year).week(weekNumber).endOf('week').format(DateFormatEnum.YYYY_MM_DD);

    const isExactWeek =
      startDate.format(DateFormatEnum.YYYY_MM_DD) === startOfWeek &&
      endDate.format(DateFormatEnum.YYYY_MM_DD) === endOfWeek;

    return isExactWeek ? weekNumber.toString() : EMPTY_STRING;
  }, [rangeDate]);
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    const params: IQueryParamSchedule = {
      startDate: rangeDate.startDate,
      endDate: rangeDate.endDate,
    };

    handleGetListSchedule(params);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeDate]);

  useEffect(() => {
    if (!isRefreshListSchedule) return;

    const params: IQueryParamSchedule = {
      startDate: rangeDate.startDate,
      endDate: rangeDate.endDate,
    };

    handleGetListSchedule(params);

    dispatch(scheduleActions.setRefreshScheduleList(false));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefreshListSchedule]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetListSchedule = (params: IQueryParamSchedule) => {
    setIsLoading(true);

    dispatch(getListSchedule(params))
      .unwrap()
      .then((res) => {
        if (!res?.data) return;

        const responses = res?.data;

        const dataConvertSchedule = convertScheduleKanban(responses, rangeDate.startDate, rangeDate.endDate);

        setDataSchedule(dataConvertSchedule);
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleChange = (dataChange: ChangeData<IScheduleList>) => {
    const { updatedItems } = dataChange;

    if (!updatedItems || updatedItems.length === 0) return;

    setIsLoading(true);

    const newEvent: IEventSchedule[] = updatedItems?.map((item) => {
      return {
        scheduleId: item.item.id,
        startDate: item.newColumnId,
        startTime: formatDate(item.item.startTime, DateFormatEnum.HH_mm),
        position: item.newIndex,
      };
    });

    const payloadChangeEventSchedule = cleanEmptyFields(newEvent) as IEventSchedule[];

    dispatch(changeEventSchedule({ events: payloadChangeEventSchedule }))
      .unwrap()
      .then((res) => {
        dispatch(scheduleActions.setRefreshScheduleList(true));
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleNextWeek = () => {
    const newRangeDate: IRangeDate = {
      startDate: moment(rangeDate.startDate).add(7, 'days').format(DateFormatEnum.YYYY_MM_DD),
      endDate: moment(rangeDate.endDate).add(7, 'days').format(DateFormatEnum.YYYY_MM_DD),
    };

    setRangeDate(newRangeDate);
  };

  const handlePreviousWeek = () => {
    const newRangeDate: IRangeDate = {
      startDate: moment(rangeDate.startDate).subtract(7, 'days').format(DateFormatEnum.YYYY_MM_DD),
      endDate: moment(rangeDate.endDate).subtract(7, 'days').format(DateFormatEnum.YYYY_MM_DD),
    };

    setRangeDate(newRangeDate);
  };

  const handleToday = () => {
    const newRangeDate = getCurrentWeekRange();
    setRangeDate(newRangeDate);
  };

  const fetchOptionFilterWeek = useCallback(async (): Promise<IResultCallApiFn<IBaseOption>> => {
    const currentYear = moment().year();
    const totalWeeks = moment().year(currentYear).weeksInYear();

    const options: IBaseOption[] = Array.from({ length: totalWeeks }, (_, index) => {
      const weekNumber = index + 1;

      return {
        value: weekNumber.toString(),
        label: `Week ${weekNumber} - ${currentYear}`,
      };
    });

    return {
      optionsResp: options,
      hasMoreResp: false,
    };
  }, []);

  const handleFilterWeek = (option: IBaseOption | null) => {
    if (!option) return;

    const currentYear = moment().year();
    const weekNumber = Number(option.value);

    const startDate = moment().year(currentYear).week(weekNumber).startOf('week').format(DateFormatEnum.YYYY_MM_DD);
    const endDate = moment().year(currentYear).week(weekNumber).endOf('week').format(DateFormatEnum.YYYY_MM_DD);

    setRangeDate({
      startDate,
      endDate,
    });
  };

  const handleExportExcel = () => {
    setIsLoading(true);

    const params: IQueryParamSchedule = {
      startDate: rangeDate.startDate,
      endDate: rangeDate.endDate,
    };

    dispatch(exportSchedule(params))
      .unwrap()
      .then((res) => {
        if (!res.data) return;

        window.open(res.data.url);
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleDisableSchedule = (isShowModal: boolean) => {
    setIsDisableSchedule(isShowModal);
  };
  //#endregion Handle Function

  return (
    <Box ref={setNodeRef} id='vehicleScheduleComponent' className={cx('container')}>
      <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
        <Box className={cx('titleGroup')}>
          <Typography className={cx('title')}>{t('schedule_title')}</Typography>
        </Box>

        {isView && (
          <Stack direction={'row'}>
            <Button
              type='button'
              variant='contained'
              className={cx('button')}
              onClick={handleExportExcel}
              startIcon={<img src={icons.commonIconExport} alt={'common_alt_img'} />}
            >
              {t('common_button_export_excel', { ns: 'common/base' })}
            </Button>
          </Stack>
        )}
      </Stack>

      {isView && (
        <Stack direction={'row'} spacing={4} marginTop={2} marginBottom={2}>
          <Stack direction={'row'}>
            <Tooltip arrow placement='top' title={t('common_button_previous_week', { ns: 'common/base' })}>
              <IconButton onClick={handlePreviousWeek} className={cx('btnChangeWeek', 'previous')}>
                <img src={icons.commonIconPrevious} alt={t('common_alt_img')} />
              </IconButton>
            </Tooltip>

            <Tooltip arrow placement='top' title={t('common_button_next_week', { ns: 'common/base' })}>
              <IconButton onClick={handleNextWeek} className={cx('btnChangeWeek', 'next')}>
                <img src={icons.commonIconNext} alt={t('common_alt_img')} />
              </IconButton>
            </Tooltip>
          </Stack>

          <Button variant='outlined' className={cx('btnToday')} onClick={handleToday}>
            {t('common_button_today', { ns: 'common/base' })}
          </Button>

          <BaseSelect
            gap={10}
            height={36}
            width={180}
            styleType='contained'
            placeholder={t('schedule_filter_title_select_week')}
            callApiFn={fetchOptionFilterWeek}
            value={selectedWeekValue}
            onChange={handleFilterWeek}
          />
        </Stack>
      )}

      <Box component={'div'} className={cx('scheduleKanban', { isOver })}>
        <BaseScheduleKanban
          disabled={isDisableSchedule}
          data={isView ? dataSchedule : []}
          onChange={handleChange}
          renderItem={(data) => <ItemSchedule item={data} onShowModal={handleDisableSchedule} />}
        />
      </Box>

      {isLoading && <Loading />}
    </Box>
  );
};

export default VehicleSchedule;
